import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AGGREGATE_OPS, aggregate, groupBy } from "../services/aggregateService";

function formatValue(value) {
  if (value == null) return "—";
  if (typeof value === "number") return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  if (typeof value?.toDate === "function") { try { return value.toDate().toLocaleString(); } catch { return String(value); } }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/**
 * Aggregate engine dialog — COUNT / SUM / AVG / MIN / MAX / DISTINCT with an
 * optional GROUP BY, computed over the currently displayed rows.
 */
export default function AggregateDialog({ open, onClose, rows, fieldNames }) {
  const [op, setOp] = useState("count");
  const [field, setField] = useState("");
  const [groupField, setGroupField] = useState("");

  const opMeta = AGGREGATE_OPS.find((o) => o.id === op) || AGGREGATE_OPS[0];
  const fields = useMemo(() => ["id", ...(fieldNames || [])], [fieldNames]);

  const grouped = useMemo(() => {
    if (!open || !groupField) return null;
    return groupBy(rows || [], { groupField, op, field });
  }, [open, groupField, rows, op, field]);

  const single = useMemo(() => {
    if (!open || groupField) return null;
    return aggregate(rows || [], { op, field });
  }, [open, groupField, rows, op, field]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Aggregate Engine</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField select size="small" label="Function" value={op} onChange={(e) => setOp(e.target.value)} sx={{ minWidth: 140 }}>
            {AGGREGATE_OPS.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Field"
            value={field}
            onChange={(e) => setField(e.target.value)}
            sx={{ minWidth: 160 }}
            disabled={!opMeta.needsField}
          >
            <MenuItem value="">(select field)</MenuItem>
            {fields.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Group by (optional)" value={groupField} onChange={(e) => setGroupField(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">(no grouping)</MenuItem>
            {fields.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </TextField>
        </Stack>

        <Typography variant="caption" color="text.secondary">
          Computed over {rows?.length || 0} displayed rows.
        </Typography>

        {single && (
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
            <Typography variant="overline" color="text.secondary">
              {opMeta.label}{opMeta.needsField && field ? ` (${field})` : ""}
            </Typography>
            <Typography variant="h4" fontWeight={800}>{formatValue(single.value)}</Typography>
            {single.count != null && <Typography variant="caption" color="text.secondary">over {single.count} numeric values</Typography>}
            {op === "distinct" && single.values && (
              <Typography variant="caption" component="div" sx={{ mt: 1, maxHeight: 160, overflow: "auto" }}>
                {single.values.join(", ")}
              </Typography>
            )}
          </Box>
        )}

        {grouped && (
          <Table size="small" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{groupField}</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">{opMeta.label}{field ? ` (${field})` : ""}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map((g) => (
                <TableRow key={g.group} hover>
                  <TableCell>{g.group}</TableCell>
                  <TableCell align="right">{g.count.toLocaleString()}</TableCell>
                  <TableCell align="right">{formatValue(g.value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
